import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    getServiceOAuth,
    upsertServiceOAuth,
    deleteServiceOAuth,
    startOAuthFlow,
    revokeOAuthToken,
} from '../../../api/services';
import {
    getAgentToken,
    deleteAgentToken,
    getAgentAuthorizationUrl,
    copyUserTokenToAgent,
} from '../../../api/agents/agent-services';
import { Lock, Plus, Trash2, Edit, ExternalLink, Copy } from 'lucide-react';
import Button from '../../common/button/Button';

import styles from './styles/ServiceDetails.module.css';

const ServiceOAuth = ({ serviceId, isServiceOwner, isAgentContext = false, agentId = null }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [oauthSettings, setOauthSettings] = useState(null);
    const [hasOAuthToken, setHasOAuthToken] = useState(false);
    const [editingOAuth, setEditingOAuth] = useState(false);

    const loadOAuthSettings = useCallback(async () => {
        try {
            setIsLoading(true);

            if (isAgentContext && agentId) {
                // For agents, only check if they have a token
                try {
                    const tokenData = await getAgentToken(agentId, serviceId);
                    setHasOAuthToken(!!tokenData);
                } catch (error) {
                    setHasOAuthToken(false);
                }

                // For OAuth settings, we still need service OAuth config (only for owners)
                if (isServiceOwner) {
                    const data = await getServiceOAuth(serviceId);
                    setOauthSettings(data.oauth);
                } else {
                    setOauthSettings(null);
                }
            } else {
                // Regular user context
                const data = await getServiceOAuth(serviceId);
                setOauthSettings(data.oauth);
                setHasOAuthToken(data.hasToken);
            }
        } catch (error) {
            setOauthSettings(false);
            console.error('Error loading OAuth settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isAgentContext, agentId, isServiceOwner]);

    useEffect(() => {
        loadOAuthSettings();
    }, [loadOAuthSettings]);

    const handleSaveOAuthSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            await upsertServiceOAuth(serviceId, {
                clientId: oauthSettings.clientId,
                clientSecret: oauthSettings.clientSecret,
                authorizationUrl: oauthSettings.authorizationUrl,
                tokenUrl: oauthSettings.tokenUrl,
                scopes: oauthSettings.scopes,
            });
            await loadOAuthSettings();
            setEditingOAuth(false);
        } catch (error) {
            console.error('Error saving OAuth settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, oauthSettings, loadOAuthSettings]);

    const handleDeleteOAuthSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            await deleteServiceOAuth(serviceId);
            setOauthSettings(null);
            setEditingOAuth(false);
        } catch (error) {
            console.error('Error deleting OAuth settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId]);

    const handleStartOAuthFlow = useCallback(async () => {
        try {
            setIsLoading(true);

            if (isAgentContext && agentId) {
                // Agent OAuth flow
                const redirectUri = `${window.location.origin}/services/oauth/callback`;
                const { authUrl } = await getAgentAuthorizationUrl(agentId, serviceId, redirectUri);

                // Store agentId and serviceId for OAuth callback
                localStorage.setItem('oauthAgentId', agentId);
                localStorage.setItem('oauthServiceId', serviceId);

                // Open OAuth authorization URL in a new window
                window.open(authUrl, '_blank');
            } else {
                // Regular user OAuth flow
                const { authorizationUrl } = await startOAuthFlow(serviceId);
                localStorage.setItem('oauthServiceId', serviceId);
                window.open(authorizationUrl, '_blank');
            }
        } catch (error) {
            console.error('Error starting OAuth flow:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isAgentContext, agentId]);

    const handleRevokeOAuthToken = useCallback(async () => {
        try {
            setIsLoading(true);

            if (isAgentContext && agentId) {
                await deleteAgentToken(agentId, serviceId);
            } else {
                await revokeOAuthToken(serviceId);
            }

            setHasOAuthToken(false);
        } catch (error) {
            console.error('Error revoking OAuth token:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isAgentContext, agentId]);

    const handleCopyUserToken = useCallback(async () => {
        try {
            setIsLoading(true);
            await copyUserTokenToAgent(agentId, serviceId);
            setHasOAuthToken(true);
        } catch (error) {
            console.error('Error copying user token to agent:', error);
            alert(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [agentId, serviceId]);

    if (isLoading && !oauthSettings) {
        return <div className={styles.text}>Loading OAuth settings...</div>;
    }

    return (
        <div className={styles.blockContainer}>
            {/* OAuth Settings (only for owners) */}
            {isServiceOwner && (
                <div className={styles.blockSection}>
                    <div className={styles.oauthHeader}>
                        <h3 className={styles.oauthTitle}>OAuth Settings</h3>
                        {!oauthSettings && !editingOAuth && (
                            <Button
                                size="small"
                                onClick={() => setEditingOAuth(true)}
                            >
                                <Plus size={14} />
                                Configure OAuth
                            </Button>
                        )}
                        {oauthSettings && !editingOAuth && (
                            <div className={styles.oauthActions}>
                                <button
                                    className={styles.editButton}
                                    onClick={() => setEditingOAuth(true)}
                                >
                                    <Edit size={14} /> Edit
                                </button>
                                <button
                                    className={styles.revokeButton}
                                    onClick={handleDeleteOAuthSettings}
                                >
                                    <Trash2 size={14} /> Delete
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={styles.secretDescription}>
                        These OAuth settings are only visible to you as the service owner. They are used to configure the OAuth flow for this service.
                    </div>

                    {editingOAuth && (
                        <div className={styles.blockEditor}>
                            <div className={styles.inputGroup}>
                                <label>Client ID:</label>
                                <input
                                    type="text"
                                    value={oauthSettings?.clientId || ''}
                                    onChange={e => setOauthSettings({
                                        ...oauthSettings || {},
                                        clientId: e.target.value,
                                    })}
                                    placeholder="Enter OAuth client ID"
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Client Secret:</label>
                                <input
                                    type="password"
                                    value={oauthSettings?.clientSecret || ''}
                                    onChange={e => setOauthSettings({
                                        ...oauthSettings || {},
                                        clientSecret: e.target.value,
                                    })}
                                    placeholder="Enter OAuth client secret"
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Authorization URL:</label>
                                <input
                                    type="text"
                                    value={oauthSettings?.authorizationUrl || ''}
                                    onChange={e => setOauthSettings({
                                        ...oauthSettings || {},
                                        authorizationUrl: e.target.value,
                                    })}
                                    placeholder="Enter authorization URL"
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Token URL:</label>
                                <input
                                    type="text"
                                    value={oauthSettings?.tokenUrl || ''}
                                    onChange={e => setOauthSettings({
                                        ...oauthSettings || {},
                                        tokenUrl: e.target.value,
                                    })}
                                    placeholder="Enter token URL"
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Scopes (comma separated):</label>
                                <input
                                    type="text"
                                    value={oauthSettings?.scopes?.join(',') || ''}
                                    onChange={(e) => setOauthSettings({
                                        ...oauthSettings || {},
                                        scopes: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                                    })}
                                    placeholder="Enter OAuth scopes"
                                />
                            </div>
                            <div className={styles.editorActions}>
                                <Button
                                    size="small"
                                    onClick={handleSaveOAuthSettings}
                                    disabled={
                                        !oauthSettings?.clientId ||
                                        !oauthSettings?.clientSecret ||
                                        !oauthSettings?.authorizationUrl ||
                                        !oauthSettings?.tokenUrl
                                    }
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => {
                                        setEditingOAuth(false);
                                        if (!oauthSettings) {
                                            setOauthSettings(null);
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}

                    {oauthSettings && !editingOAuth && (
                        <div className={styles.oauthDetails}>
                            <div className={styles.oauthDetail}>
                                <strong>Client ID:</strong> {oauthSettings.clientId}
                            </div>
                            <div className={styles.oauthDetail}>
                                <strong>Client Secret:</strong> ••••••••••••••••
                            </div>
                            <div className={styles.oauthDetail}>
                                <strong>Authorization URL:</strong> {oauthSettings.authorizationUrl}
                            </div>
                            <div className={styles.oauthDetail}>
                                <strong>Token URL:</strong> {oauthSettings.tokenUrl}
                            </div>
                            <div className={styles.oauthDetail}>
                                <strong>Scopes:</strong> {oauthSettings.scopes?.join(', ') || 'None'}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* OAuth Authorization for users */}
            <div className={styles.blockSection}>
                <h3 className={styles.oauthTitle}>
                    {isAgentContext ? 'Agent OAuth Authorization' : 'OAuth Authorization'}
                </h3>
                <div className={styles.secretDescription}>
                    {isAgentContext 
                        ? 'Configure OAuth authorization for this agent. You can authorize the agent directly or copy your existing authorization.'
                        : 'Authorize your account to use this service.'
                    }
                </div>

                {hasOAuthToken ? (
                    <div className={styles.oauthStatus}>
                        <div className={styles.successText}>
                            <Lock size={14} /> 
                            {isAgentContext ? 'Agent is authorized' : 'Your account is authorized'}
                        </div>
                        <button
                            className={styles.revokeButton}
                            onClick={handleRevokeOAuthToken}
                        >
                            Revoke Access
                        </button>
                    </div>
                ) : (
                    <div className={styles.oauthActions}>
                        <Button
                            size="small"
                            onClick={handleStartOAuthFlow}
                            disabled={oauthSettings === false}
                        >
                            <ExternalLink size={12} />
                            {isAgentContext ? 'Authorize Agent' : 'Authorize'}
                        </Button>

                        {isAgentContext && (
                            <Button
                                variant="secondary"
                                size="small"
                                onClick={handleCopyUserToken}
                                disabled={oauthSettings === false}
                            >
                                <Copy size={12} />
                                Copy My Authorization
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(ServiceOAuth);
